import { useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle,
  BookOpen,
  Volume2,
  Play,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectModuleById } from "@/store/selectors/meetingModulesSelectors";
import { getMeetingModules } from "@/store/slices/meetingModulesSlice";
import { getMeetingSections } from "@/store/slices/meetingSectionSlice";
import { clearPreparationAnswers } from "@/store/slices/meetingSectionSlice";
import {
  submitMeetingPreparation,
  preloadAudioFiles,
  resetPreparationSubmission,
} from "@/store/slices/meetingSlice";
import {
  loadPreparationData,
  clearPreparationData,
  isPreparationComplete,
} from "@/utils/sessionStorageManager";
import { PreparationAnswer } from "@/types/meeting-preparation.types";
import { useToast } from "@/hooks/use-toast";
import { useProduct } from "@/hooks/useProduct";

const MeetingPreparationSummary = () => {
  const config = useProduct();
  const { meetingId } = useParams(); // This is the section ID from URL
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  // Detect if coming from practice expressions completion
  const searchParams = new URLSearchParams(location.search);
  const fromPractice = searchParams.get("from") === "practice";

  const moduleData = useAppSelector(selectModuleById(meetingId || ""));
  const preparationState = useAppSelector(
    (state) => state.meeting.preparationSubmission
  );

  // Get the actual realtime module ID from sections
  const sections = useAppSelector((state) => state.meetingSection.sections);
  const sectionsLoading = useAppSelector((state) => state.meetingSection.loading);
  const realtimeModuleId = sections[0]?.id || null;

  // FIXED: Move all hooks outside conditionals - hooks must be called in the same order every render
  const categories = useAppSelector((state) => state.meetingModules.categories);

  // Calculate actualModuleId without conditional hooks
  // Use the currentModuleId from state if available, otherwise use meetingId
  const currentModuleId = useAppSelector(
    (state) => state.meetingSection.currentModuleId
  );
  const actualModuleId = currentModuleId || meetingId;

  // Try to get module data with the corrected moduleId
  const correctedModuleData = useAppSelector(
    selectModuleById(actualModuleId || "")
  );

  // Always load meeting modules on mount (needed for selectModuleById to work)
  useEffect(() => {
    dispatch(getMeetingModules({}));
  }, [dispatch]);

  // Load meeting sections when we don't have module data or when coming from practice
  useEffect(() => {
    if (meetingId && (!moduleData || fromPractice)) {
      dispatch(getMeetingSections(meetingId));
    }
  }, [meetingId, moduleData, fromPractice, dispatch]);

  // Submit preparation on mount
  useEffect(() => {
    const validateAndSubmit = async () => {
      // Only generate expressions if coming directly from preparation (no 'from' query param)
      // If 'from' param exists, we're viewing existing expressions from Dashboard/other sources
      if (searchParams.has("from")) {
        return;
      }

      // Check if we have preparation data
      const sessionData = loadPreparationData();

      if (!sessionData || !meetingId) {
        toast({
          title: config.i18n.meetingPreparationSummary.noData,
          description: config.i18n.meetingPreparationSummary.noData,
          variant: "destructive",
        });
        navigate(`/meeting/${meetingId}/prepare`);
        return;
      }

      // Validate that preparation is complete
      if (
        !isPreparationComplete(sessionData.answers, sessionData.totalQuestions)
      ) {
        toast({
          title: config.i18n.meetingPreparationSummary.incomplete,
          description: config.i18n.meetingPreparationSummary.incomplete,
          variant: "destructive",
        });
        navigate(`/meeting/${meetingId}/prepare`);
        return;
      }

      // Format answers for API v2 backend (question + answer pairs)
      const answersArray: PreparationAnswer[] = Object.entries(
        sessionData.questions
      ).map(([complementId, questionText]) => ({
        question: questionText,
        answer: sessionData.answers[complementId] || "",
      }));

      // Check if we have the realtime module ID
      if (!realtimeModuleId) {
        if (sectionsLoading) {
          return; // Still loading sections, wait for the effect to re-run
        }
        toast({
          title: config.i18n.meetingPreparationSummary.configError,
          description: config.i18n.meetingPreparationSummary.configError,
          variant: "destructive",
        });
        navigate(`/meeting/${meetingId}/prepare`);
        return;
      }

      // Submit to backend via Redux thunk
      dispatch(
        submitMeetingPreparation({
          moduleId: realtimeModuleId,
          answers: answersArray,
          meetingId: meetingId || "",
        })
      );
    };

    validateAndSubmit();
  }, [meetingId, realtimeModuleId, sectionsLoading, navigate, dispatch, toast]);

  // Preload audio files after expressions are fetched
  useEffect(() => {
    if (
      preparationState.status === "fetching" &&
      preparationState.expressions &&
      preparationState.expressions.length > 0
    ) {
      dispatch(preloadAudioFiles(preparationState.expressions));
    }
  }, [preparationState.status, preparationState.expressions, dispatch]);

  // Handle successful completion
  useEffect(() => {
    if (preparationState.status === "success") {
      // NOTE: We DON'T clear data here anymore
      // User should be able to navigate back to summary with data intact
      // Data will only be cleared when starting a NEW meeting preparation

      toast({
        title: config.i18n.meetingPreparationSummary.submitSuccess,
        description: config.i18n.meetingPreparationSummary.submitSuccess,
      });
    }
  }, [
    preparationState.status,
    toast,
    config.i18n.meetingPreparationSummary.submitSuccess,
  ]);

  // Save expressions to sessionStorage for use in simulation
  useEffect(() => {
    if (
      preparationState.status === "success" &&
      preparationState.expressions &&
      preparationState.expressions.length > 0
    ) {
      try {
        sessionStorage.setItem(
          `meeting_expressions_${meetingId}`,
          JSON.stringify(preparationState.expressions)
        );
      } catch (error) {
        // Failed to save expressions to sessionStorage
      }
    }
  }, [preparationState.status, preparationState.expressions, meetingId]);

  // Handle errors
  useEffect(() => {
    if (preparationState.status === "error" && preparationState.error) {
      toast({
        title: config.i18n.meetingPreparationSummary.submitFailed,
        description: preparationState.error,
        variant: "destructive",
      });
    }
  }, [
    preparationState.status,
    preparationState.error,
    toast,
    config.i18n.meetingPreparationSummary.submitFailed,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Don't reset here - we want to keep the data for PracticeExpressions
      // Only reset when starting a new preparation (done in meetingSlice)
    };
  }, []);

  // Handle retry on error
  const handleRetry = () => {
    dispatch(resetPreparationSubmission());
    window.location.reload();
  };

  // Loading states
  if (preparationState.status === "submitting") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">
            {config.i18n.meetingPreparationSummary.submitting}
          </h2>
          <p className="text-muted-foreground">
            {config.i18n.meetingPreparationSummary.submittingDesc}
          </p>
        </div>
      </div>
    );
  }

  if (preparationState.status === "pooling") {
    const progress = Math.min(
      (preparationState.poolingAttempts / 40) * 100,
      90
    );
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-6">
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {config.i18n.meetingPreparationSummary.generatingExpressions}
          </h2>
          <p className="text-muted-foreground mb-6">
            {config.i18n.meetingPreparationSummary.generatingExpressionsDesc}
          </p>
          <div className="space-y-2">
            <Progress value={progress} className="h-2" />
            <p className="text-sm text-muted-foreground">
              {config.i18n.meetingPreparationSummary.attemptOf(
                preparationState.poolingAttempts,
                40
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (preparationState.status === "loading-audio" && !fromPractice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-6">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">
            {config.i18n.meetingPreparationSummary.loadingAudio}
          </h2>
          <p className="text-muted-foreground mb-4">
            {config.i18n.meetingPreparationSummary.loadingAudioDesc(
              preparationState.expressions?.length || 0
            )}
          </p>
          <div className="space-y-2">
            <Progress
              value={preparationState.audioLoadingProgress}
              className="h-2"
            />
            <p className="text-sm text-muted-foreground">
              {config.i18n.meetingPreparationSummary.percentComplete(
                preparationState.audioLoadingProgress
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (preparationState.status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {config.i18n.meetingPreparationSummary.submissionFailedTitle}
          </h2>
          <p className="text-muted-foreground mb-6">
            {preparationState.error ||
              config.i18n.meetingPreparationSummary.unexpectedError}
          </p>
          <div className="flex gap-4 justify-center">
            <Button onClick={handleRetry}>
              {config.i18n.meetingPreparationSummary.tryAgain}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/meeting/${meetingId}/prepare`)}
            >
              {config.i18n.meetingPreparationSummary.backToPreparation}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Check if categories are loaded (already declared at line 53)
  const categoriesLoaded = categories.length > 0;

  // Loading state while categories are being loaded
  if (!categoriesLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {config.i18n.meetingPreparationSummary.loadingDetails}
          </p>
        </div>
      </div>
    );
  }

  // Use corrected module data if available
  const finalModuleData = correctedModuleData || moduleData;

  // No module data (only show error after categories are loaded)
  if (!finalModuleData && categoriesLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">
            {config.i18n.meetingPreparationSummary.notFound}
          </p>
          {/* <Button onClick={() => navigate("/")}>Back to Home</Button> */}
        </div>
      </div>
    );
  }

  const { module } = finalModuleData;
  const expressions = preparationState.expressions || [];
  const hasExpressions = expressions.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-6">
      <div className="max-w-3xl mx-auto">
        <div className="bg-card/80 backdrop-blur-sm rounded-lg shadow-lg p-8 border border-border/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                {config.i18n.meetingPreparationSummary.greatWork}
              </h1>
              <p className="text-muted-foreground">{module.module_name}</p>
            </div>
          </div>

          {hasExpressions ? (
            <div className="mb-8">
              <p className="text-lg text-foreground mb-4">
                {config.i18n.meetingPreparationSummary.expressionsDesc}
              </p>
              <div className="space-y-4 border-l-2 border-primary/30 pl-4">
                {expressions.map((expression, index) => (
                  <div key={expression.id} className="space-y-1">
                    <p className="text-sm font-semibold text-primary">
                      {config.i18n.meetingPreparationSummary.expression}{" "}
                      {index + 1}
                    </p>
                    <p className="text-foreground leading-relaxed">
                      {expression.expression}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-8 p-4 bg-muted/50 rounded-lg">
              <p className="text-muted-foreground text-center">
                No specific expressions were generated. You can still practice
                or start the simulation.
              </p>
            </div>
          )}

          <h1 className="text-3xl font-bold text-foreground text-center mb-8">
            {config.i18n.meetingPreparationSummary.whatNext}
          </h1>

          <div className="grid md:grid-cols-2 gap-6">
            <Card
              className="group relative overflow-hidden transition-all duration-300 hover:shadow-[var(--shadow-hover)] border-border/50 backdrop-blur-sm bg-card/80 cursor-pointer"
              onClick={() =>
                navigate(`/meeting/${meetingId}/practice-expressions`)
              }
            >
              <div className="p-8">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Volume2 className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3">
                  {config.i18n.meetingPreparationSummary.practiceExpressions}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {
                    config.i18n.meetingPreparationSummary
                      .practiceExpressionsDesc
                  }
                </p>
                <Button
                  variant={fromPractice ? "outline" : "default"}
                  className={`w-full ${fromPractice ? "group-hover:bg-primary group-hover:text-primary-foreground" : "group-hover:bg-accent group-hover:text-accent-foreground group-hover:border-accent"}`}
                >
                  {fromPractice
                    ? config.i18n.meetingPreparationSummary.tryExpressionsAgain
                    : config.i18n.meetingPreparationSummary.practiceExpressions}
                </Button>
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </Card>

            <Card
              className="group relative overflow-hidden transition-all duration-300 hover:shadow-[var(--shadow-hover)] border-border/50 backdrop-blur-sm bg-card/80 cursor-pointer"
              onClick={() => navigate(`/meeting/${meetingId}/simulate`)}
            >
              <div className="p-8">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Play className="w-6 h-6 text-accent" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3">
                  {config.i18n.meetingPreparationSummary.startSimulation}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {config.i18n.meetingPreparationSummary.startSimulationDesc}
                </p>
                <Button
                  variant={fromPractice ? "default" : "outline"}
                  className={`w-full ${fromPractice ? "group-hover:bg-accent group-hover:text-accent-foreground group-hover:border-accent" : "group-hover:bg-primary group-hover:text-primary-foreground"}`}
                >
                  {config.i18n.meetingPreparationSummary.startSimulation}
                </Button>
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingPreparationSummary;
